import type { ToastRootToastObject } from '@base-ui/react/toast';
import type { ReactNode } from 'react';
import { Toast as BaseToast } from '@base-ui/react/toast';

import type { IconName } from '../../assets/Icon/Icon';
import type { VariantProp } from '../../common/colors';
import { Icon } from '../../assets/Icon/Icon';
import { LoadingIcon } from '../../assets/LoadingIcon/LoadingIcon';
import {
  getVariantStyles,
  variantBackgroundColor,
  variantBodyColor,
  variantHeadingColor,
  variantIconColor,
  variantOverlayShadow,
} from '../../common/variant';
import { zIndexes } from '../../common/z_indexes';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import { colors, darkThemeSelector, keyframes, styled } from '../../stitches.config';
import { Body } from '../../text/Body';
import { Small } from '../../text/Small';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { isDefined } from '../../utilities/isDefined';
import { VStack } from '../../utilities/Stack/VStack';

const toastIconRunningSpin = keyframes({
  '50%': { transform: 'rotate(180deg)' },
  '100%': { transform: 'rotate(360deg)' },
});

const toastIconStyles = {
  width: '$14',
  height: '$14',
  color: variantIconColor,
};

const ToastIconRunning = styled(Icon, {
  ...toastIconStyles,
  animation: `${toastIconRunningSpin} 2s linear infinite`,
});
ToastIconRunning.displayName = 'ToastIconRunning';

const ToastIconLoading = styled(LoadingIcon, {
  ...toastIconStyles,
});
ToastIconLoading.displayName = 'ToastIconLoading';

const ToastIconGlyph = styled(Icon, {
  ...toastIconStyles,
});
ToastIconGlyph.displayName = 'ToastIconGlyph';

const ToastHeading = styled(Body, {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$4',
  color: variantHeadingColor,
  fontWeight: '$bold',
});
ToastHeading.displayName = 'ToastHeading';

const ToastDescription = styled(Small, {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '$4',
  color: variantBodyColor,
});
ToastDescription.displayName = 'ToastDescription';

export interface ToastContentProps {
  /**
   * The actions to display in the toast.
   */
  actions?: ReactNode;
  /**
   * The description to display in the toast.
   */
  description?: ReactNode;
  /**
   * The heading to display in the toast.
   */
  heading: ReactNode;
  /**
   * The icon to display in the toast.
   */
  icon?: IconName;
  /**
   * The state of the toast's related event.
   */
  state?: 'loading' | 'running';
  /**
   * The variant of the toast.
   */
  variant?: VariantProp;
}

function ToastIcon({
  icon,
  state,
}: Omit<ToastContentProps, 'actions' | 'heading' | 'description' | 'variant'>) {
  if (state === 'loading') {
    return <ToastIconLoading />;
  }
  if (state === 'running') {
    return <ToastIconRunning icon="arrows-rotate" />;
  }
  if (icon) {
    return <ToastIconGlyph icon={icon} />;
  }

  return null;
}

function ToastLayout({
  actions,
  description,
  heading,
  icon,
  state,
}: Omit<ToastContentProps, 'variant'>) {
  return (
    <AlignStack
      direction="row"
      gap={{
        start: 6,
        end: 8,
      }}
      preset="body"
      start={(isDefined(icon) || isDefined(state)) && <ToastIcon icon={icon} state={state} />}
      end={
        isDefined(actions) && (
          <ControlGroup relation="separate" size="small">
            {actions}
          </ControlGroup>
        )
      }
    >
      <VStack>
        {heading && <BaseToast.Title render={<ToastHeading />}>{heading}</BaseToast.Title>}
        {description && (
          <BaseToast.Description render={<ToastDescription />}>{description}</BaseToast.Description>
        )}
      </VStack>
    </AlignStack>
  );
}

const TOAST_STACK_GAP_PX = 8;
const TOAST_STACK_PEEK_PX = 12;

export const ToastViewport = styled(BaseToast.Viewport, {
  position: 'fixed',
  bottom: '$20',
  left: 0,
  width: '100vw',
  zIndex: zIndexes.alert,
});
ToastViewport.displayName = 'ToastViewport';

// The --toast-* values are set inline by Base UI's Toast.Root/Toast.Viewport.
const ToastRoot = styled('div', {
  $$stackScale: 'max(0, 1 - (var(--toast-index) * 0.1))',
  $$stackShrink: 'min(1, var(--toast-index) * 0.1)',
  $$stackHeight: 'var(--toast-frontmost-height, var(--toast-height))',
  $$stackExpandedY: `calc((var(--toast-offset-y) * -1) - (var(--toast-index) * ${TOAST_STACK_GAP_PX}px) + var(--toast-swipe-movement-y))`,
  position: 'absolute',
  bottom: 0,
  left: '50%',
  zIndex: `calc(${zIndexes.alert} - var(--toast-index))`,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  width: 'max-content',
  maxWidth: '$720',
  height: '$$stackHeight',
  padding: '$8 $12',
  backgroundColor: variantBackgroundColor,
  boxShadow: variantOverlayShadow,
  borderRadius: '$10',
  transformOrigin: 'bottom center',
  transition: 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.5s, height 0.15s',
  transform: `translateX(calc(-50% + var(--toast-swipe-movement-x))) translateY(calc(var(--toast-swipe-movement-y) - (var(--toast-index) * ${TOAST_STACK_PEEK_PX}px) - ($$stackShrink * $$stackHeight))) scale($$stackScale)`,

  '&[data-expanded]': {
    height: 'var(--toast-height)',
    transform:
      'translateX(calc(-50% + var(--toast-swipe-movement-x))) translateY($$stackExpandedY)',
  },

  '&[data-starting-style], &[data-ending-style]': {
    transform: 'translateX(-50%) translateY(150%)',
  },

  '&[data-limited]': {
    opacity: 0,
  },

  '&[data-ending-style]': {
    opacity: 0,

    "&[data-swipe-direction='up']": {
      transform: 'translateX(-50%) translateY(calc(var(--toast-swipe-movement-y) - 150%))',
    },
    "&[data-swipe-direction='down']": {
      transform: 'translateX(-50%) translateY(calc(var(--toast-swipe-movement-y) + 150%))',
    },
    "&[data-swipe-direction='left']": {
      transform:
        'translateX(calc(-50% + var(--toast-swipe-movement-x) - 150%)) translateY($$stackExpandedY)',
    },
    "&[data-swipe-direction='right']": {
      transform:
        'translateX(calc(-50% + var(--toast-swipe-movement-x) + 150%)) translateY($$stackExpandedY)',
    },
  },

  // Invisible bridge over the gap below so hover-expansion doesn't flicker between toasts
  '&::after': {
    content: "''",
    position: 'absolute',
    top: '100%',
    left: 0,
    width: '100%',
    height: `${TOAST_STACK_GAP_PX + 1}px`,
  },

  variants: {
    variant: {
      alternative: {
        ...getVariantStyles('alternative'),
      },
      attention: {
        ...getVariantStyles('attention'),
      },
      brand: {
        ...getVariantStyles('brand'),
      },
      negative: {
        ...getVariantStyles('negative'),
      },
      neutral: {
        backgroundColor: colors.bgApplicationLight,

        [darkThemeSelector]: {
          backgroundColor: colors.bgApplicationDark,
        },

        // Allows to inherit the variant styles without breaking the prior art
        '&': {
          ...getVariantStyles('neutral'),
        },
      },
      positive: {
        ...getVariantStyles('positive'),
      },
    },
  },
});
ToastRoot.displayName = 'ToastRoot';

const ToastBody = styled(BaseToast.Content, {
  overflow: 'hidden',
  // Bleed the clip box to the card edge; action controls and focus rings paint outside the line box
  padding: '$8 $12',
  margin: '-$8 -$12',
  transition: 'opacity 0.25s',

  '&[data-behind]': {
    opacity: 0,
  },

  '&[data-expanded]': {
    opacity: 1,
  },
});
ToastBody.displayName = 'ToastBody';

export interface ToastProps extends ToastContentProps {
  /**
   * The Base UI toast object this toast renders, from `useToastManager()`.
   */
  toast: ToastRootToastObject;
}

/**
 * Renders a Base UI toast inside a `ToastViewport`. Collapsed toasts peek out behind the
 * frontmost one and expand into a list when the viewport is hovered or focused.
 */
export function Toast({
  actions,
  description,
  heading,
  icon,
  state,
  toast,
  variant = 'neutral',
}: ToastProps) {
  return (
    <ToastRoot
      as={BaseToast.Root}
      toast={toast}
      swipeDirection={['down', 'left', 'right']}
      variant={variant}
    >
      <ToastBody>
        <ToastLayout
          actions={actions}
          description={description}
          heading={heading}
          icon={icon}
          state={state}
        />
      </ToastBody>
    </ToastRoot>
  );
}
